/**
 * @module agent/adapters/services/CallbackManager
 *
 * Manages callback registration, invocation, and cleanup for the AgentCoreAdapter event system.
 * Extracted from AgentCoreAdapter to reduce adapter complexity.
 *
 * Handles four callback types:
 * - message: New messages (user, AI, errors)
 * - stateChange: Adapter state transitions (idle, thinking, responding, searching)
 * - streamingUpdate: Streaming chunk updates during response generation
 * - phaseChange: Message phase transitions (thinking -> responding -> complete)
 * - sessionsRefreshed: Backend session list settled (TD-2571)
 */

/**
 * Manages callback registration and notification for adapter events.
 */
class CallbackManager {
  /**
   * @param {Object} [initialCallbacks] - Optional initial callbacks to register
   * @param {Function} [initialCallbacks.onMessage] - Initial message callback
   * @param {Function} [initialCallbacks.onStateChange] - Initial state change callback
   * @param {Function} [initialCallbacks.onStreamingUpdate] - Initial streaming update callback
   * @param {Function} [initialCallbacks.onPhaseChange] - Initial phase change callback
   */
  constructor(initialCallbacks = {}) {
    this._messageCallbacks = [];
    this._stateCallbacks = [];
    this._streamingUpdateCallbacks = [];
    this._phaseChangeCallbacks = [];
    this._searchProgressCallbacks = [];
    this._sessionsRefreshedCallbacks = [];

    // Register initial callbacks if provided
    if (initialCallbacks.onMessage) {
      this.onMessage(initialCallbacks.onMessage);
    }
    if (initialCallbacks.onStateChange) {
      this.onStateChange(initialCallbacks.onStateChange);
    }
    if (initialCallbacks.onStreamingUpdate) {
      this.onStreamingUpdate(initialCallbacks.onStreamingUpdate);
    }
    if (initialCallbacks.onPhaseChange) {
      this.onPhaseChange(initialCallbacks.onPhaseChange);
    }
  }

  // ============================================================================
  // Registration Methods
  // ============================================================================

  /**
   * Register a callback for message events.
   * @param {Function} callback - Callback to invoke on message events
   * @returns {Function} Unsubscribe function to remove the callback
   * @throws {TypeError} If callback is not a function
   */
  onMessage(callback) {
    this._validateCallback(callback, 'onMessage');
    this._messageCallbacks.push(callback);
    return () => this._removeCallback(this._messageCallbacks, callback);
  }

  /**
   * Register a callback for state change events.
   * @param {Function} callback - Callback to invoke on state changes
   * @returns {Function} Unsubscribe function to remove the callback
   * @throws {TypeError} If callback is not a function
   */
  onStateChange(callback) {
    this._validateCallback(callback, 'onStateChange');
    this._stateCallbacks.push(callback);
    return () => this._removeCallback(this._stateCallbacks, callback);
  }

  /**
   * Register a callback for streaming update events.
   * @param {Function} callback - Callback to invoke on streaming updates
   * @returns {Function} Unsubscribe function to remove the callback
   * @throws {TypeError} If callback is not a function
   */
  onStreamingUpdate(callback) {
    this._validateCallback(callback, 'onStreamingUpdate');
    this._streamingUpdateCallbacks.push(callback);
    return () => this._removeCallback(this._streamingUpdateCallbacks, callback);
  }

  /**
   * Register a callback for phase change events.
   * @param {Function} callback - Callback to invoke on phase changes
   * @returns {Function} Unsubscribe function to remove the callback
   * @throws {TypeError} If callback is not a function
   */
  onPhaseChange(callback) {
    this._validateCallback(callback, 'onPhaseChange');
    this._phaseChangeCallbacks.push(callback);
    return () => this._removeCallback(this._phaseChangeCallbacks, callback);
  }

  /**
   * Register a callback for search progress events.
   * @param {Function} callback - Callback to invoke on search progress updates
   * @returns {Function} Unsubscribe function to remove the callback
   * @throws {TypeError} If callback is not a function
   */
  onSearchProgress(callback) {
    this._validateCallback(callback, 'onSearchProgress');
    this._searchProgressCallbacks.push(callback);
    return () => this._removeCallback(this._searchProgressCallbacks, callback);
  }

  /**
   * Register a callback for session-list settle events (TD-2571).
   * @param {Function} callback - Callback to invoke when the backend session list settles
   * @returns {Function} Unsubscribe function to remove the callback
   * @throws {TypeError} If callback is not a function
   */
  onSessionsRefreshed(callback) {
    this._validateCallback(callback, 'onSessionsRefreshed');
    this._sessionsRefreshedCallbacks.push(callback);
    return () => this._removeCallback(this._sessionsRefreshedCallbacks, callback);
  }

  // ============================================================================
  // Notification Methods
  // ============================================================================

  /**
   * Notify message callbacks with a new message.
   * Passes through ALL messages (including hidden) so subscribers can decide how to handle.
   *
   * ## Migration Guide (TD-1845)
   * Previously (before TD-1845): Hidden messages were skipped and callbacks were not invoked.
   * After TD-1845: ALL messages are passed through with `hidden` flag in event payload.
   *
   * **Breaking Change**: Subscribers MUST handle the `hidden` property in event payload.
   * - Check `event.hidden` before rendering message to UI
   * - Hidden messages should trigger UI updates (to remove from view) but not be displayed
   *
   * **Why this changed**: Skipping hidden messages prevented UI from updating when messages
   * transitioned from visible to hidden (e.g., search initiation message). This caused stale
   * UI where hidden messages remained visible until next render trigger.
   *
   * Fix #23: Simplified event payload - access message properties via event.message object.
   * Flat properties (messageId, messageType, content) removed to prevent redundancy.
   *
   * @param {string} sessionId - Session ID the message belongs to
   * @param {Object} message - Message object (access message.id, message.type, message.content)
   * @param {string} message.id - Message ID
   * @param {string} [message.type] - Message type (user, ai, agent)
   * @param {string} [message.content] - Message content
   * @param {boolean} [message.hidden] - Whether message is hidden from UI
   */
  notifyMessage(sessionId, message) {
    // TD-1845: Pass through ALL messages, let subscribers decide visibility handling.
    // This ensures UI updates when messages transition to hidden state.
    // Fix #23: Simplified payload - subscribers access message properties via event.message
    this._invokeCallbacks(
      this._messageCallbacks,
      {
        type: 'message',
        sessionId,
        message,
        hidden: message.hidden || false
      },
      'message callback'
    );
  }

  /**
   * Notify state change callbacks with session context.
   *
   * TD-1904: State changes now include sessionId so the provider can
   * track per-session state instead of global state.
   *
   * @param {string} sessionId - Session ID the state change belongs to
   * @param {string} newState - New adapter state (idle, thinking, responding, searching)
   */
  notifyStateChange(sessionId, newState) {
    this._invokeCallbacks(this._stateCallbacks, { sessionId, state: newState }, 'state callback');
  }

  /**
   * Notify error to message callbacks.
   *
   * @param {Object} errorInfo - Error information
   * @param {string} errorInfo.type - Error type
   * @param {string} [errorInfo.message] - Error message
   */
  notifyError(errorInfo) {
    this._invokeCallbacks(
      this._messageCallbacks,
      {
        type: 'error',
        error: errorInfo,
        timestamp: new Date()
      },
      'error callback'
    );
  }

  /**
   * Notify streaming update callbacks.
   *
   * @param {string} sessionId - Session ID
   * @param {Object} message - Message being updated
   * @param {string} chunk - New chunk of data
   */
  notifyStreamingUpdate(sessionId, message, chunk) {
    this._invokeCallbacks(
      this._streamingUpdateCallbacks,
      {
        type: 'streaming_update',
        sessionId,
        messageId: message.id,
        chunk,
        message,
        timestamp: new Date()
      },
      'streaming callback'
    );
  }

  /**
   * Notify phase change callbacks.
   *
   * @param {Object} event - Phase change event
   * @param {string} event.phase - New phase
   * @param {string} [event.messageId] - Message ID
   */
  notifyPhaseChange(event) {
    this._invokeCallbacks(this._phaseChangeCallbacks, event, 'phase callback');
  }

  /**
   * Notify search progress callbacks with session context.
   *
   * TD-1904: Search progress now includes sessionId so the provider can
   * track per-session search state.
   *
   * @param {string} sessionId - Session ID the search belongs to
   * @param {Object} event - Search progress event
   * @param {string} event.phase - Current search phase (from SEARCH_PROGRESS_PHASES)
   * @param {number} event.targetProgress - Target progress percentage (0-100)
   */
  notifySearchProgress(sessionId, event) {
    this._invokeCallbacks(
      this._searchProgressCallbacks,
      { sessionId, ...event },
      'search progress callback'
    );
  }

  /**
   * TD-2571: Notify that the backend session list has settled.
   *
   * @param {Object} event - Settle event
   * @param {Array} event.sessions - Sessions as currently known
   * @param {boolean} event.ok - Whether the backend was actually reached
   */
  notifySessionsRefreshed(event) {
    this._invokeCallbacks(this._sessionsRefreshedCallbacks, event, 'sessions refreshed callback');
  }

  // ============================================================================
  // Cleanup
  // ============================================================================

  /**
   * Clear all callbacks and reset to initial state.
   * Safe to call multiple times.
   */
  destroy() {
    this._messageCallbacks = [];
    this._stateCallbacks = [];
    this._streamingUpdateCallbacks = [];
    this._phaseChangeCallbacks = [];
    this._searchProgressCallbacks = [];
    this._sessionsRefreshedCallbacks = [];
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  /**
   * Validate that a callback is a function.
   * @param {*} callback - Value to validate
   * @param {string} methodName - Method name for error message
   * @throws {TypeError} If callback is not a function
   * @private
   */
  _validateCallback(callback, methodName) {
    if (typeof callback !== 'function') {
      throw new TypeError(`CallbackManager.${methodName}: callback must be a function`);
    }
  }

  /**
   * Remove a callback from an array.
   * @param {Array} callbacks - Callback array
   * @param {Function} callback - Callback to remove
   * @private
   */
  _removeCallback(callbacks, callback) {
    const index = callbacks.indexOf(callback);
    if (index !== -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * Invoke all callbacks with error handling.
   * Errors in individual callbacks are caught and logged, allowing other callbacks to continue.
   *
   * @param {Array<Function>} callbacks - Callbacks to invoke
   * @param {*} data - Data to pass to callbacks
   * @param {string} [name='callback'] - Callback name for error logging
   * @performance Fix #6: Creates defensive copy on each invocation to prevent
   *              modification-during-iteration bugs. Avoid registering/unregistering
   *              callbacks during high-frequency notifications (e.g., streaming updates).
   * @private
   */
  _invokeCallbacks(callbacks, data, name = 'callback') {
    // Create a copy to handle modifications during iteration (defensive, see @performance)
    const callbacksCopy = [...callbacks];

    callbacksCopy.forEach((cb) => {
      try {
        cb(data);
      } catch (error) {
        console.error(`Error in ${name}:`, error);
      }
    });
  }
}

export default CallbackManager;
